'use client';
import React from 'react';
import Link from 'next/link';
import PublicTopbar from '@/components/PublicTopbar';
import PublicFooter from '@/components/PublicFooter';
import { Shield, ArrowLeft } from 'lucide-react';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-navy-950 flex flex-col">
      <PublicTopbar />
      <main className="flex-1 pt-20 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Back */}
          <Link href="/home" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors mb-8">
            <ArrowLeft size={16} />
            Back to Home
          </Link>

          {/* Header */}
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-brand-500/15 border border-brand-500/25 flex items-center justify-center">
              <Shield size={20} className="text-brand-400" />
            </div>
            <span className="chip">Legal</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-800 text-white mt-3 mb-2">
            Privacy <span className="text-gradient">Policy</span>
          </h1>
          <p className="text-slate-400 text-sm mb-10">Last updated: May 11, 2026</p>

          <div className="space-y-8 text-slate-300 text-sm leading-relaxed">

            <section>
              <h2 className="font-display text-lg font-700 text-white mb-3">1. Introduction</h2>
              <p>Take a Zip LLC ("ZipAdBook," "we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services at zipadbook.com.</p>
            </section>

            <section>
              <h2 className="font-display text-lg font-700 text-white mb-3">2. Information We Collect</h2>
              <p className="mb-3 font-medium text-white">2.1 Information You Provide Directly</p>
              <ul className="list-disc pl-5 space-y-1.5 mb-4">
                <li>Account registration data: name, business name, email address, phone number, and password.</li>
                <li>Order and payment information: billing address, payment card details (processed and stored by Stripe — we do not store raw card data).</li>
                <li>Artwork and creative assets you upload for advertising campaigns.</li>
                <li>Communications you send us via support tickets, contact forms, or email.</li>
              </ul>
              <p className="mb-3 font-medium text-white">2.2 Information Collected Automatically</p>
              <ul className="list-disc pl-5 space-y-1.5 mb-4">
                <li>Log data: IP address, browser type, pages visited, time spent, referring URLs.</li>
                <li>Device information: device type, operating system, unique device identifiers.</li>
                <li>Cookies and similar tracking technologies (see Section 6).</li>
                <li>Usage analytics via Google Analytics (anonymized).</li>
              </ul>
              <p className="mb-3 font-medium text-white">2.3 Information from Third Parties</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Payment processors (Stripe): transaction status, payment method type, fraud signals.</li>
                <li>Authentication providers: if you sign in via a third-party OAuth provider.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-lg font-700 text-white mb-3">3. How We Use Your Information</h2>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>To create and manage your account and authenticate your identity.</li>
                <li>To process orders, payments, and deliver advertising services.</li>
                <li>To send transactional emails (order confirmations, artwork reminders, delivery notifications).</li>
                <li>To respond to your support requests and inquiries.</li>
                <li>To improve our platform, diagnose technical issues, and analyze usage trends.</li>
                <li>To send marketing communications (only with your consent; you may opt out at any time).</li>
                <li>To comply with legal obligations and enforce our Terms of Service.</li>
                <li>To detect and prevent fraud, abuse, and security incidents.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-lg font-700 text-white mb-3">4. How We Share Your Information</h2>
              <p className="mb-2">We do not sell your personal information. We may share your data with:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li><strong className="text-white">Service providers:</strong> Stripe (payments), Supabase (database/auth), Resend (email delivery), Vercel (hosting). These providers are contractually obligated to protect your data.</li>
                <li><strong className="text-white">Business transfers:</strong> In connection with a merger, acquisition, or sale of assets, your data may be transferred as part of that transaction.</li>
                <li><strong className="text-white">Legal requirements:</strong> When required by law, court order, or governmental authority.</li>
                <li><strong className="text-white">Protection of rights:</strong> To protect the rights, property, or safety of ZipAdBook, our users, or the public.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-lg font-700 text-white mb-3">5. Data Retention</h2>
              <p>We retain your personal data for as long as your account is active or as needed to provide services. Account data is retained for 3 years after account closure for legal and business purposes. Payment records are retained for 7 years as required by tax law. You may request deletion of your data at any time (see Section 8).</p>
            </section>

            <section>
              <h2 className="font-display text-lg font-700 text-white mb-3">6. Cookies & Tracking Technologies</h2>
              <p className="mb-2">We use cookies and similar technologies to:</p>
              <ul className="list-disc pl-5 space-y-1.5 mb-3">
                <li><strong className="text-white">Essential cookies:</strong> Required for authentication, session management, and core platform functionality. Cannot be disabled.</li>
                <li><strong className="text-white">Analytics cookies:</strong> Google Analytics to understand how users interact with our platform (anonymized). You may opt out.</li>
                <li><strong className="text-white">Preference cookies:</strong> Remember your settings and preferences across sessions.</li>
              </ul>
              <p>You can manage cookie preferences through our Cookie Consent banner or your browser settings. Note that disabling essential cookies may impair platform functionality.</p>
            </section>

            <section>
              <h2 className="font-display text-lg font-700 text-white mb-3">7. Data Security</h2>
              <p>We implement industry-standard security measures including TLS encryption in transit, AES-256 encryption at rest (via Supabase), row-level security policies, and regular security audits. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.</p>
            </section>

            <section>
              <h2 className="font-display text-lg font-700 text-white mb-3">8. Your Rights & Choices</h2>
              <p className="mb-2">Depending on your location, you may have the following rights:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li><strong className="text-white">Access:</strong> Request a copy of the personal data we hold about you.</li>
                <li><strong className="text-white">Correction:</strong> Request correction of inaccurate or incomplete data.</li>
                <li><strong className="text-white">Deletion:</strong> Request deletion of your personal data ("right to be forgotten").</li>
                <li><strong className="text-white">Portability:</strong> Request your data in a machine-readable format.</li>
                <li><strong className="text-white">Opt-out of marketing:</strong> Unsubscribe from marketing emails at any time via the link in any email or through Account Settings.</li>
                <li><strong className="text-white">Cookie preferences:</strong> Manage analytics and preference cookies via our Cookie Consent banner.</li>
              </ul>
              <p className="mt-3">To exercise these rights, email us at <a href="mailto:privacy@zipadbook.com" className="text-brand-400 hover:text-brand-300 transition-colors">privacy@zipadbook.com</a>. We will respond within 30 days.</p>
            </section>

            <section>
              <h2 className="font-display text-lg font-700 text-white mb-3">9. Children's Privacy</h2>
              <p>Our Service is not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that a child has provided us with personal information, we will delete it promptly.</p>
            </section>

            <section>
              <h2 className="font-display text-lg font-700 text-white mb-3">10. California Privacy Rights (CCPA)</h2>
              <p>California residents have additional rights under the California Consumer Privacy Act (CCPA), including the right to know what personal information is collected, the right to delete personal information, and the right to opt out of the sale of personal information. We do not sell personal information. To exercise your CCPA rights, contact us at <a href="mailto:privacy@zipadbook.com" className="text-brand-400 hover:text-brand-300 transition-colors">privacy@zipadbook.com</a>.</p>
            </section>

            <section>
              <h2 className="font-display text-lg font-700 text-white mb-3">11. Changes to This Policy</h2>
              <p>We may update this Privacy Policy from time to time. We will notify you of material changes by email or by posting a prominent notice on our platform. Your continued use of the Service after changes take effect constitutes acceptance of the updated policy.</p>
            </section>

            <section>
              <h2 className="font-display text-lg font-700 text-white mb-3">12. Contact Us</h2>
              <p>For privacy-related questions or to exercise your rights:</p>
              <div className="mt-3 p-4 rounded-xl bg-navy-800 border border-white/8">
                <p className="text-white font-medium">Privacy Officer — Take a Zip LLC / ZipAdBook</p>
                <p className="mt-1">Email: <a href="mailto:privacy@zipadbook.com" className="text-brand-400 hover:text-brand-300 transition-colors">privacy@zipadbook.com</a></p>
                <p>Phone: <a href="tel:+13212653177" className="text-brand-400 hover:text-brand-300 transition-colors">+1 (321) 265-3177</a></p>
              </div>
            </section>

          </div>

          <div className="mt-12 pt-8 border-t border-white/8 flex flex-col sm:flex-row gap-4">
            <Link href="/terms-of-service" className="inline-flex items-center gap-2 text-sm text-brand-400 hover:text-brand-300 transition-colors">
              View Terms of Service →
            </Link>
            <Link href="/customer-support" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors">
              Contact Support →
            </Link>
          </div>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}
